import { zodResolver } from '@hookform/resolvers/zod';
import { Accessible, LocalParking, Pets, PregnantWoman } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField } from '../../../../../components/atoms/input-field';
import { CreateVisitVisitorFormValues } from '../../organisms/create-visit/schema/create-visit-validation-schema';

import { DiscountTemplate } from '@/features/hosts/api/types/discount';
import { VISITOR_LANGUAGE } from '@/features/visitors/api/types/visitor';
import { useCreateVisitValidationSchema } from '../../organisms/create-visit/schema/create-visit-validation-schema';

enum BUSINESS_OPTIONS_FIELD_NAMES {
  IS_HANDICAPPED = 'isHandicapped',
  IS_PREGNANT = 'isPregnant',
  HAS_ACCESS_TO_PARKING = 'hasAccessToParking',
  HAS_PET = 'hasPet',
}

const businessOptions = [
  {
    title: 'createVisit:additionalInformation.handicappedTitle' as const,
    content: 'createVisit:additionalInformation.handicappedContent' as const,
    icon: <Accessible />,
    name: BUSINESS_OPTIONS_FIELD_NAMES.IS_HANDICAPPED,
  },
  {
    title: 'createVisit:additionalInformation.pregnantTitle' as const,
    content: 'createVisit:additionalInformation.pregnantContent' as const,
    icon: <PregnantWoman />,
    name: BUSINESS_OPTIONS_FIELD_NAMES.IS_PREGNANT,
  },
  {
    title: 'createVisit:additionalInformation.hasPetTitle' as const,
    content: 'createVisit:additionalInformation.hasPetContent' as const,
    icon: <Pets />,
    name: BUSINESS_OPTIONS_FIELD_NAMES.HAS_PET,
  },
  {
    title: 'createVisit:additionalInformation.parkingTitle' as const,
    content: 'createVisit:additionalInformation.parkingContent' as const,
    icon: <LocalParking />,
    name: BUSINESS_OPTIONS_FIELD_NAMES.HAS_ACCESS_TO_PARKING,
  },
];

const languageOptions = [
  {
    value: VISITOR_LANGUAGE.POLISH,
    title: 'createVisit:visitors.pl' as const,
  },
  {
    value: VISITOR_LANGUAGE.ENGLISH,
    title: 'createVisit:visitors.en' as const,
  },
];

const formatDiscountValue = (discount: DiscountTemplate, fullCoverageLabel: string) => {
  switch (discount.discountType) {
    case 'percentage':
      return discount.discountValue === null ? null : `${discount.discountValue}%`;
    case 'fixed_amount':
      if (discount.discountValue === null) return null;

      return discount.currency
        ? `${discount.currency} ${discount.discountValue.toFixed(2)}`
        : String(discount.discountValue);
    case 'full_coverage':
      return fullCoverageLabel;
    case null:
      return discount.code;
  }
};

const getDiscountLabel = (
  discount: DiscountTemplate,
  fullCoverageLabel: string,
  durationLabel: string | null,
) =>
  [discount.name, formatDiscountValue(discount, fullCoverageLabel), durationLabel]
    .filter(Boolean)
    .join(' · ');

interface Props {
  allowInviteToParking: boolean;
  discountTemplates: DiscountTemplate[];
  onCancel: () => void;
  onSubmit: (visitor: CreateVisitVisitorFormValues) => void;
  visitor: CreateVisitVisitorFormValues;
}

export const VisitorInfoForm = ({
  allowInviteToParking,
  discountTemplates,
  onCancel,
  onSubmit,
  visitor,
}: Props) => {
  const { t } = useTranslation(['common', 'createVisit', 'validation']);
  const { visitorSchema } = useCreateVisitValidationSchema();

  const {
    control,
    handleSubmit,
    setValue,
    watch: watchVisitor,
    formState: { isSubmitting },
  } = useForm<CreateVisitVisitorFormValues>({
    defaultValues: visitor,
    resolver: zodResolver(visitorSchema),
    mode: 'onBlur',
  });
  const visitorValues = watchVisitor();

  const isSubmitDisabled =
    isSubmitting || !visitorValues.firstName || !visitorValues.lastName || !visitorValues.email;

  const isDiscountDisabled = !visitorValues.hasAccessToParking || discountTemplates.length === 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card
        variant="outlined"
        sx={{
          p: 2,
        }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridAutoFlow: 'row',
          }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              control={control}
              name="firstName"
              render={({ field, fieldState: { error } }) => (
                <InputField
                  {...field}
                  label={t('createVisit:visitors.firstName')}
                  fullWidth
                  required
                  error={!!error?.message}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="lastName"
              render={({ field, fieldState: { error } }) => (
                <InputField
                  {...field}
                  label={t('createVisit:visitors.lastName')}
                  fullWidth
                  required
                  error={!!error?.message}
                  helperText={error?.message}
                />
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <InputField
                  {...field}
                  label={'Email'}
                  fullWidth
                  required
                  error={!!error?.message}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="language"
              render={({ field, fieldState: { error } }) => (
                <InputField
                  {...field}
                  label={t('createVisit:visitors.language')}
                  fullWidth
                  required
                  select
                  error={!!error?.message}
                  helperText={error?.message}>
                  {languageOptions?.map(language => (
                    <MenuItem key={language.value} value={language.value}>
                      {t(language.title)}
                    </MenuItem>
                  ))}
                </InputField>
              )}
            />
          </Box>
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                error={!!error?.message}
                fullWidth
                helperText={error?.message}
                label={t('createVisit:visitors.description')}
                multiline
                rows={2}
              />
            )}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('createVisit:visitors.additionalInformation')}
            </Typography>
            {businessOptions.map(option => {
              const { content, icon: Icon, name, title } = option;
              const isParkingDisabled =
                allowInviteToParking === false &&
                name === BUSINESS_OPTIONS_FIELD_NAMES.HAS_ACCESS_TO_PARKING;

              return (
                <Controller
                  key={`${visitor.localId}-${name}`}
                  control={control}
                  name={name}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <Tooltip
                        title={
                          isParkingDisabled
                            ? t('createVisit:additionalInformation.parkingDisabledTooltip')
                            : ''
                        }>
                        <Card
                          aria-disabled={isParkingDisabled}
                          onClick={() => {
                            if (!isParkingDisabled) {
                              const hasAccessToParking = !value;

                              onChange(hasAccessToParking);

                              if (!hasAccessToParking) {
                                setValue('discountTemplateId', null, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }
                            }
                          }}
                          key={content}
                          sx={{
                            boxShadow: theme =>
                              value
                                ? `0px 0px 0px 2px ${theme.palette.primary.main}`
                                : `0px 0px 0px 1px ${theme.palette.divider}`,
                            cursor: isParkingDisabled ? 'not-allowed' : 'pointer',
                            mb: 2,
                            opacity: isParkingDisabled ? 0.55 : 1,
                          }}>
                          <CardContent
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                            }}>
                            <Avatar
                              variant="rounded"
                              sx={{
                                backgroundColor: 'background.default',
                                borderColor: 'divider',
                                borderStyle: 'solid',
                                borderWidth: 1,
                                mr: 2,
                              }}>
                              {Icon}
                            </Avatar>
                            <Box>
                              <Typography color="textPrimary" sx={{ mb: 1 }} variant="h6">
                                {t(title)}
                              </Typography>
                              <Typography color="textSecondary" variant="body1">
                                {t(content)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Tooltip>
                    );
                  }}
                />
              );
            })}
            <Controller
              control={control}
              name="discountTemplateId"
              render={({ field, fieldState: { error } }) => (
                <Tooltip
                  title={isDiscountDisabled ? t('createVisit:visitors.noDiscountsAvailable') : ''}
                  disableHoverListener={!isDiscountDisabled}
                  disableFocusListener={!isDiscountDisabled}>
                  <InputField
                    {...field}
                    disabled={isDiscountDisabled}
                    error={!!error?.message}
                    fullWidth
                    helperText={error?.message}
                    label={t('createVisit:visitors.discount')}
                    onChange={event =>
                      field.onChange(event.target.value === '' ? null : Number(event.target.value))
                    }
                    select
                    SelectProps={{ displayEmpty: true }}
                    value={field.value ?? ''}>
                    <MenuItem value="">{t('createVisit:visitors.noDiscount')}</MenuItem>
                    {discountTemplates.map(discount => (
                      <MenuItem key={discount.id} value={discount.id}>
                        {getDiscountLabel(
                          discount,
                          t('createVisit:visitors.fullCoverage'),
                          discount.timeLimitMinutes
                            ? t('createVisit:visitors.upToMinutes', {
                                count: discount.timeLimitMinutes,
                              })
                            : null,
                        )}
                      </MenuItem>
                    ))}
                  </InputField>
                </Tooltip>
              )}
            />{' '}
          </Box>
        </Box>
      </Card>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          pt: 3,
        }}>
        <Button
          color="primary"
          onClick={onCancel}
          size="large"
          sx={{ mr: 2 }}
          type="button"
          variant="text">
          {t('common:button.back')}
        </Button>
        <Button
          color="primary"
          size="large"
          type="submit"
          variant="contained"
          disabled={isSubmitDisabled}>
          {t('common:button.save')}
        </Button>
      </Box>
    </form>
  );
};
